import React, { useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Button, Divider, ToolPanel } from 'Components/readonly'

import { FieldError, FlexRow, FlexRowCenter, Section, Spacer } from 'Components/layout'
import { AuthContext } from 'Context'
import { Field, Form } from 'Components/form'
import { Checkbox, FileUpload, MarketLanguageDropdown } from 'Components/input'
import ImportPreview from './importPreview'
import styled from 'styled-components'

const NoMarginForm = styled(Form)`
  & > * {
    margin-bottom: 0;
  }
`

const FileRowComponent = ({ value, onChange }) => {
  const internalOnChange = (e, prop) => {
    const newValue = { ...value, [prop]: e.target.value }
    onChange({ target: { value: newValue } })
  }
  // Removed from FlexRow
  // <Datepicker value={value.date} onChange={(e) => internalOnChange(e, 'date')}/>
  return (
    <FlexRow>
      <MarketLanguageDropdown value={value.language} onChange={(e) => internalOnChange(e, 'language')}/>
    </FlexRow>
  )
}

const SelectSds = ({
  products,
  errors,
  checkedProductsForSdsUpload,
  onProductCheckChange,
  importSds,
  setSdsForImportProduct,
  userLanguage3,
  uploadText,
}) => (
  products.map((product) => (
    <Section key={product.productKey}>
      <div style={{ position: 'relative' }}>

        {errors[product.productKey] && <FieldError/>}

        <div>
          <Checkbox
            label={product.name}
            description={
              <>
                <FormattedMessage id="common.supplier" />
                  : {product.supplier}<br/>
                <FormattedMessage id="common.locations" />
                  :
                  <ul>
                  {product.locations.map((location) => (
                    <li key={location.name}>
                      {location.name}
                    </li>
                  ))}
                  </ul>
              </>
            }
            value={checkedProductsForSdsUpload[product.productKey]}
            onChange={(e) => onProductCheckChange(product.productKey, e.target.checked)}
          />
        </div>
        {checkedProductsForSdsUpload[product.productKey] && (
          <FlexRowCenter style={{ marginLeft: '25px' }}>
            <FileUpload
              id={'register_sds_' + product.productKey}
              text={uploadText}
              multiple={true}
              FileRowComponent={FileRowComponent}
              intialExtraValue={{ language: userLanguage3 }}
              value={importSds[product.productKey]}
              onChange={(e) => setSdsForImportProduct(product.productKey, e.target.value)}
            />
          </FlexRowCenter>
        )}
      </div>
      <Divider/>
    </Section>
  ))
)

export default ({ data, importType, closePreview, setSdsForImportProduct }) => {
  const intl = useIntl()
  const { userLanguage3 } = useContext(AuthContext)
  const [isDone, setIsDone] = useState(false)
  const [errors, setErrors] = useState({})
  const newProducts = useMemo(() => data.products.filter((product) => product.id === undefined), [data.products])
  const existingProducts = useMemo(() => data.products.filter((product) => product.id !== undefined), [data.products])

  const gotoNextStep = () => {
    setIsDone(true)
  }

  useEffect(() => {
    if (newProducts.length === 0 && existingProducts.length === 0) {
      gotoNextStep()
    }
  })

  const [checkedProductsForSdsUpload, setCheckedProductsForSdsUpload] = useState(() => {
    const initialCheckedProducts = {}
    newProducts.forEach((product) => {
      initialCheckedProducts[product.productKey] = true
    })
    return initialCheckedProducts
  })

  const onProductCheckChange = (productKey, checked) => {
    if (!checked) {
      setSdsForImportProduct(productKey, [])
    }
    setCheckedProductsForSdsUpload((prev) => ({
      ...prev,
      [productKey]: checked
    }))
  }

  const selectSdsProps = {
    errors,
    checkedProductsForSdsUpload,
    onProductCheckChange,
    importSds: data.importSds,
    setSdsForImportProduct,
    userLanguage3,
    uploadText: intl.formatMessage({ id: 'sds.upload_sdb' }),
  }

  const validateFiles = () => {
    let collectedErrors = {}
    let firstError = null

    data.products.forEach((product, index) => {
      // for all checked products,

      if (checkedProductsForSdsUpload[product.productKey]) {
        // check if file and language is set correctly
        if (
          data.importSds[product.productKey].length === 0 ||
          data.importSds[product.productKey].filter((file) => {
            return !file.deleted && !file.extra.language
          }).length > 0
        ) {
          collectedErrors[product.productKey] = true
          // if not return validation error
          if (!firstError) {
            firstError = { id: 'chemicals.language_required' }
          }
        }

        // Detect language duplicates
        const languages = data.importSds[product.productKey].filter((file) => !file.deleted).map((file) => file.extra.language)
        if (data.importSds[product.productKey].length === 0 || languages.some((language, i) => languages.indexOf(language) !== i)) {
          collectedErrors[product.productKey] = true
          if (!firstError) {
            firstError = { id: 'validation.distinct', values: { attribute: 'common.language' } }
          }
        }
      }
    })

    setErrors(collectedErrors)
    return firstError
  }

  const onSubmit = () => {
    gotoNextStep()
  }

  return isDone ? (
    <ImportPreview data={data} importType={importType} closePreview={closePreview}/>
  ) : (
    <>
      <h2>
        {importType === 'archived' && (
          <FormattedMessage id="chemicals.import.upload-archived-sds"/>
        )}
        {importType === 'active' && (
          <FormattedMessage id="chemicals.import.upload-sds"/>
        )}
      </h2>
      <NoMarginForm onSubmit={onSubmit} validation>
        {newProducts.length > 0 && (
          <p>
            <FormattedMessage id="chemicals.import.upload-sds-text"/>
          </p>
        )}
        <Field
          id="new-product"
          Component={SelectSds}
          products={newProducts}
          importType={importType}
          hasErrorHandling={true}
          validation={() => validateFiles(newProducts)}
          {...selectSdsProps}
        />
        {existingProducts.length > 0 && (
          <p>
            <FormattedMessage id="chemicals.import.upload-sds-existing-text"/>
          </p>
        )}
        <Field
          id="existing-product"
          Component={SelectSds}
          products={existingProducts}
          importType={importType}
          hasErrorHandling={true}
          validation={() => validateFiles(existingProducts)}
          {...selectSdsProps}
        />
        <ToolPanel>
          <Spacer/>
          <Button label={intl.formatMessage({ id: 'common.cancel' })} onClick={closePreview}/>
          <Button primary submit label={intl.formatMessage({ id: 'common.continue' })}/>
        </ToolPanel>
      </NoMarginForm>
    </>
  )
}
